import axios from 'axios';

const ANTHROPIC_API_URL = 'https://api.anthropic.com/v1/messages';
const ANTHROPIC_VERSION = '2023-06-01';

/**
 * Complete iRefer Reference Database - 259 Codes
 * Royal College of Radiologists Guidelines
 */
const IREFER_REFERENCE_DATABASE = `
================================================================================
COMPLETE IREFER REFERENCE CODES - COMPREHENSIVE LIST
Royal College of Radiologists iRefer Guidelines
================================================================================

[BREAST IMAGING - B CODES]

B01 | Breast screening: women <40 years old
Category: Breast
Modality: Mammography - NOT INDICATED [B]
Radiation Dose: ☢ (ionising radiation)
Indication: Screening asymptomatic women under 40
Evidence: No evidence supports routine screening <40 unless increased risk
Exception: BRCA mutation, strong family history, previous chest radiation
Recommendation: Only for high-risk patients after genetic counselling

B02 | Breast screening: women 40-49 years old
Category: Breast
Modality: Mammography - INDICATED ONLY IN SPECIFIC CIRCUMSTANCES [A]
Radiation Dose: ☢ (ionising radiation)
Indication: Screening women aged 40-49
Evidence: High-quality evidence but contentious (false positives, overdiagnosis)
Requirement: Counselling recommended regarding false positives and overdiagnosis issues
Recommendation: Individual risk assessment required

B03 | Breast screening: women 50-70 years old
Category: Breast
Modality: Mammography - INDICATED [A]
Radiation Dose: ☢ (ionising radiation)
Indication: Routine breast cancer screening
Evidence: Strong RCT evidence supports screening, maximum benefit-to-risk ratio
Interval: 3 years in UK national programme
Recommendation: Routine screening recommended

B04 | Breast screening: women >70 years old
Category: Breast
Modality: Mammography - INDICATED [A]
Radiation Dose: ☢ (ionising radiation)
Indication: Screening elderly women
Evidence: No RCT evidence for >70, small benefit may be outweighed by overdiagnosis
Consideration: Shorter life expectancy and tumor biology factors
Recommendation: Individual assessment based on health status

B05 | Breast screening: women with increased lifetime risk
Category: Breast
Clinical Context: Family history, prior mediastinal radiotherapy

Modality 1: Mammography (including advanced digital techniques e.g., tomosynthesis)
- Indication: INDICATED [B]
- Radiation Dose: ☢ (ionising radiation)
- Notes: Referral to breast clinic should precede imaging
- Use: Within triple assessment
- Appropriate: ≥40 years
- Technique: Implant-displaced views if possible

Modality 2: Ultrasound
- Indication: INDICATED [B]
- Radiation Dose: None
- Notes: <40 imaging of choice within triple assessment
- Use: Also useful ≥40 for discrete lesion
- Guides: Biopsy procedures

Modality 3: MRI
- Indication: SPECIALISED INVESTIGATION [B]
- Radiation Dose: None
- Notes: Problem-solving when prior assessment indeterminate
- Important: Not substitute for biopsy

Modality 4: Mammography (screening pathway / moderate risk context)
- Indication: SPECIALISED INVESTIGATION [B]
- Radiation Dose: ☢ (ionising radiation)
- Notes: Annual screening only after genetic risk assessment/counselling
- High lifetime risk: Prior mediastinal RT <30 offered surveillance
- Moderate risk: Offered screening per national guidance

B06 | Breast cancer: diagnosis
Category: Breast
Clinical Triggers: Breast lump, focal nodularity, skin tethering, new nipple retraction, bloody/single-duct discharge, suspected Paget's disease
Modality: Mammography + Ultrasound + Biopsy - INDICATED [A]
Radiation Dose: ☢ (mammography only)
Context: Triple assessment (clinical + imaging + pathology)
Recommendation: Full investigation required for any suspicious clinical finding

B07 | Breast generalised pain/tenderness, lumpiness without localised changes
Category: Breast
Clinical Context: Long-standing nipple retraction, no focal abnormality

Modality 1: Mammography
- Indication: INDICATED ONLY IN SPECIFIC CIRCUMSTANCES [C]
- Radiation Dose: ☢ (ionising radiation)
- Notes: Recommended for women >40 with persisting non-suspicious symptoms

Modality 2: Ultrasound
- Indication: INDICATED ONLY IN SPECIFIC CIRCUMSTANCES [C]
- Radiation Dose: None
- Notes: In absence of focal signs, unlikely to affect management

B08 | Cyclical mastalgia
Category: Breast
Clinical Context: Cyclical breast pain related to menstrual cycle

Modality 1: Mammography
- Indication: NOT INDICATED [B]
- Radiation Dose: ☢ (ionising radiation)
- Notes: No specific indications in absence of clinical concern
- Follow: Usual age-based guidelines (B01-B05)

Modality 2: Ultrasound
- Indication: NOT INDICATED [B]
- Radiation Dose: None
- Notes: No specific indications in absence of clinical concern
- Follow: Usual age-based guidelines

B09 | Breast inflammation
Category: Breast
Clinical Context: Suspected abscess or severe mastitis
Modality: Ultrasound - INDICATED [C]
Radiation Dose: None
Notes: Patients should be referred to symptomatic breast clinic for triple assessment
Uses: Abscess diagnosis, guide drainage, follow-up, and biopsy if suspicious
Recommendation: Primary imaging modality for inflammatory breast conditions

B10 | Assessment of integrity of breast implants
Category: Breast
Clinical Context: Suspected rupture, capsular contracture, or other complication

Modality 1: Ultrasound
- Indication: SPECIALISED INVESTIGATION [B]
- Radiation Dose: None
- Notes: Only image if focal symptoms/signs or rupture finding changes management
- Use: US initial for leak/rupture; MRI if equivocal

Modality 2: Mammography
- Indication: SPECIALISED INVESTIGATION [C]
- Radiation Dose: ☢ (ionising radiation)
- Notes: May help within triple assessment if suspicion of malignancy

Modality 3: MRI
- Indication: INDICATED ONLY IN SPECIFIC CIRCUMSTANCES [C]
- Radiation Dose: None
- Notes: May help if ongoing suspicion of malignancy
- Gold standard: For silicone rupture detection

B11 | Male patients with breast symptoms
Category: Breast
Clinical Context: Breast lump, asymmetric breast tissue, nipple discharge
Modality: Mammography + Ultrasound - INDICATED [A]
Radiation Dose: ☢ (mammography only)
Notes: Requires same investigation pathway as female patients
Context: Triple assessment in specialist breast service
Recommendation: Full diagnostic workup for any male breast abnormality

[CANCER IMAGING - CA CODES]

CA00 | Paraneoplastic syndrome: oncogenic osteomalacia
Category: Cancer
Modality: MRI - SPECIALISED [C]
Modality: PET-CT/SPECT-CT - SPECIALISED [C]
Use: Anatomical characterization after functional imaging identifies tumor

CA01 | Brain and spinal cord tumors: diagnosis
Category: Cancer/Neurological
Modality: MRI - INDICATED [B] INVESTIGATION OF CHOICE
Modality: CT - INDICATED [B] (if MRI not feasible)
Indication: Suspected intracranial or intraspinal tumor
Advantage: MRI superior (may miss small tumors with CT)

CA02 | Oral cavity and pharynx cancer: diagnosis
Category: Cancer/Head & Neck
Modality: MRI with contrast - SPECIALISED [B] PREFERRED
Modality: CT with contrast - SPECIALISED [B] (if MRI contraindicated)
Primary diagnosis: Clinical examination and endoscopy with biopsy
Imaging: Supports when high suspicion of occult disease

CA03 | Major salivary gland tumors: diagnosis
Category: Cancer/Head & Neck
Modality: Ultrasound + Biopsy - INDICATED [A] FIRST-LINE
Modality: MRI - SPECIALISED [B] (after US, for staging)
Modality: CT - SPECIFIC CIRCUMSTANCES [B]
Note: No imaging reliably differentiates benign from malignant

CA04 | Larynx cancer: diagnosis
Category: Cancer/Head & Neck
Modality: CT with contrast - SPECIFIC CIRCUMSTANCES [B]
Modality: MRI with contrast - SPECIFIC CIRCUMSTANCES [B]
Use: Usually for staging (diagnosis by endoscopy)
Timing: Imaging before biopsy if diagnosis equivocal

CA06 | Lung cancer: diagnosis
Category: Cancer/Respiratory
Modality: Chest X-ray - INDICATED [A] INITIAL TEST
Modality: CT Chest - INDICATED [A]
Indication: Hemoptysis, persistent cough, recurrent infection, weight loss
CT: Higher sensitivity than CXR, identifies early-stage disease
Screening: Low-dose CT for high-risk (age 55-80, 30 pack-years)

CA07 | Oesophageal cancer: diagnosis
Category: Cancer/GI
Modality: Endoscopy - INDICATED [C] BEST INVESTIGATION
Modality: Contrast swallow - SPECIFIC CIRCUMSTANCES [B]
Modality: CT - SPECIFIC CIRCUMSTANCES [B]
Use: When endoscopy cannot be performed

CA08 | Stomach cancer: diagnosis
Category: Cancer/GI
Modality: Endoscopy - INDICATED [B] BEST INVESTIGATION
Modality: Double contrast barium meal - SPECIFIC CIRCUMSTANCES [B]
Modality: CT - SPECIFIC CIRCUMSTANCES [B]
Use: When endoscopy cannot be performed

CA09 | Liver lesion: diagnosis
Category: Cancer/GI
Modality: Ultrasound - INDICATED [B] MAY BE INITIAL
Modality: MRI - SPECIFIC CIRCUMSTANCES [B] BEST FOR CHARACTERIZATION
Modality: CT - SPECIFIC CIRCUMSTANCES [B]
Use: US for HCC surveillance, MRI superior for characterization

CA11 | Pancreatic cancer: diagnosis
Category: Cancer/GI
Modality: CT - INDICATED [B] INITIAL INVESTIGATION OF CHOICE
Modality: MRI/MRCP - INDICATED [B]
Modality: Endoscopic US - SPECIALISED [C]
Protocol: Pancreatic protocol CT with multiple phases

CA12 | Colorectal cancer: diagnosis
Category: Cancer/GI
Modality: Colonoscopy - INDICATED [A] INVESTIGATION OF CHOICE
Modality: CT Colonography - INDICATED [A]
Modality: MRI - SPECIALISED [B] (RECTAL staging ONLY, not diagnosis)
Note: MRI should NOT be used as primary diagnostic test

CA14 | Anal cancer: diagnosis
Category: Cancer/GI
Clinical Context: Anal mass, bleeding, pain, or suspicious lesion

Modality 1: Proctoscopy
- Indication: INDICATED [B]
- Radiation Dose: None
- Notes: Direct examination + biopsy should be first line
- Recommendation: Primary diagnostic approach

Modality 2: MRI
- Indication: INDICATED ONLY IN SPECIFIC CIRCUMSTANCES [C]
- Radiation Dose: None
- Notes: Complex cases; assist biopsy targeting; used for local staging
- Use: After tissue diagnosis for staging

Modality 3: Barium enema
- Indication: NOT INDICATED [C]
- Radiation Dose: ☢☢ (moderate ionising radiation)
- Notes: Less sensitive; superseded by colonoscopy and CT colonography
- Recommendation: Avoid - outdated technique

CA15 | Kidney cancer: detection and diagnosis
Category: Cancer/Urological
Clinical Context: Renal mass, hematuria, flank pain, incidental finding

Modality 1: Ultrasound
- Indication: INDICATED [B]
- Radiation Dose: None
- Notes: Frequently first investigation; detects masses >2 cm
- Discriminates: Cystic vs solid
- Contrast agents: May help complex lesions
- Use: Follow-up and surveillance

Modality 2: CT
- Indication: INDICATED [B]
- Radiation Dose: ☢☢ (moderate ionising radiation)
- Notes: Contrast-enhanced CT sensitive >0.75 cm
- Key for: Characterising solid/complex cystic masses
- Gold standard: For renal mass characterization

Modality 3: MRI
- Indication: SPECIALISED INVESTIGATION [B]
- Radiation Dose: None
- Notes: Useful for detection/characterisation/surveillance
- Particularly when: Radiation or iodinated contrast contraindicated
- Alternative: When CT not suitable

CA16 | Bladder cancer: diagnosis
Category: Cancer/Urological
Clinical Context: Painless hematuria, recurrent UTIs, bladder mass

Modality 1: Ultrasound
- Indication: INDICATED ONLY IN SPECIFIC CIRCUMSTANCES [B]
- Radiation Dose: None
- Notes: Cystoscopy is investigation of choice
- Detects: Larger tumours/hydronephrosis if CT/MRI/cystoscopy not possible
- Limitation: Less sensitive than cystoscopy

Modality 2: CT (including CT urography)
- Indication: INDICATED ONLY IN SPECIFIC CIRCUMSTANCES [B]
- Radiation Dose: ☢☢ (moderate ionising radiation)
- Notes: Exclude coexistent upper tract tumours
- Use: Stage gross local and distant disease
- Protocol: CT urography for complete urinary tract assessment

Modality 3: MRI
- Indication: INDICATED ONLY IN SPECIFIC CIRCUMSTANCES [B]
- Radiation Dose: None
- Notes: Lesion characterisation; more accurate local staging
- Advantage: Superior soft tissue contrast for staging

CA17 | Prostate cancer: diagnosis
Category: Cancer/Urological
Clinical Context: Elevated PSA, abnormal DRE, LUTS
Modality: MRI Prostate (mpMRI) - INDICATED [A]
Radiation Dose: None
Notes: Cross-references U10; mpMRI is investigation of choice
Scoring: PI-RADS scoring system for lesion characterization
Pre-biopsy: MRI recommended before biopsy to guide targeting
Recommendation: Gold standard for prostate cancer detection and staging

CA18 | Testicular cancer: diagnosis
Category: Cancer/Urological
Modality: Ultrasound - INDICATED [B] INVESTIGATION OF CHOICE
Modality: MRI - SPECIFIC CIRCUMSTANCES [B]
Use: MRI for indeterminate masses (avoid unnecessary orchidectomy)
Adjunct: Serum tumor markers

CA19 | Ovarian cancer: diagnosis
Category: Cancer/Gynaecological
Modality: Ultrasound (TVUS + TA) - INDICATED [B] FIRST-LINE
Modality: MRI - SPECIALISED [B]
Protocol: Both transabdominal AND transvaginal scanning
Adjunct: Ca125 and RMI calculation (>200 high risk)

CA20 | Cervical cancer: diagnosis
Category: Cancer/Gynaecological
Primary diagnosis: Clinical with cytology/histopathology
Modality: MRI - SPECIFIC CIRCUMSTANCES [B]
Indication: Tumors ≥5mm/Stage 1B or greater
Use: Local staging, parametrial invasion (NOT diagnosis)

CA21 | Uterine cancer (endometrial): diagnosis
Category: Cancer/Gynaecological
Modality: Ultrasound (TVUS) - INDICATED [B]
Modality: MRI - INDICATED [B]
Indication: TVUS for postmenopausal bleeding, MRI for staging
Primary diagnosis: Hysteroscopy, D&C, or endometrial biopsy

CA22 | Lymphoma: diagnosis
Category: Cancer/Haematological
Modality: Ultrasound - INDICATED [B] (for biopsy guidance)
Modality: CT - INDICATED [B] (for staging)
Process: Biopsy first, then CT for staging
Note: Specialist MDT discussion required

[CARDIOVASCULAR - CV CODES]

CV01 | Acute chest pain: suspected acute coronary syndrome
Category: Cardiovascular
Modality: Chest X-ray - INDICATED [B]
Modality: CT Coronary Angiography - SPECIFIC CIRCUMSTANCES [B]
Use: Low-moderate risk, exclude coronary disease

CV02 | Chronic chest pain: suspected coronary artery disease
Category: Cardiovascular
Modality: CT Coronary Angiography - INDICATED [B]
Alternative: Functional imaging (stress echo, MPS, stress MRI)

CV03 | Atrial fibrillation: structural heart disease
Category: Cardiovascular
Modality: Echocardiography - INDICATED [A]
Use: Assess cardiac structure and function

CV04 | Heart failure
Category: Cardiovascular
Modality: Echocardiography - INDICATED [A] FIRST-LINE
Modality: Chest X-ray - INDICATED [B]
Modality: CMR - SPECIFIC CIRCUMSTANCES [B]

CV05 | Pulmonary embolism: suspected
Category: Cardiovascular
Modality: CTPA - INDICATED [A] GOLD STANDARD
Modality: V/Q scan - SPECIFIC CIRCUMSTANCES [B]
Prerequisite: Moderate-high pre-test probability OR positive D-dimer

CV06 | Deep vein thrombosis: suspected
Category: Cardiovascular
Modality: Compression ultrasound with Doppler - INDICATED [A]
Sensitivity: >95% for proximal DVT

CV07 | Abdominal aortic aneurysm: screening
Category: Cardiovascular
Modality: Ultrasound - INDICATED [A]
Population: Men age 65
Threshold: 5.5cm for surgical referral

CV08 | Abdominal aortic aneurysm: surveillance
Category: Cardiovascular
Modality: Ultrasound - INDICATED [A]
Indication: Known AAA <5.5cm diameter

CV09 | Aortic dissection: suspected
Category: Cardiovascular
Modality: CT Angiography - INDICATED [A]
Modality: MRI - INDICATED [A] (alternative)
Use: Emergency diagnosis, classification

[GASTROINTESTINAL - GI CODES]

GI01 | Dysphagia
Category: Gastrointestinal
Modality: Barium swallow - INDICATED [B]
Modality: Endoscopy - INDICATED [A] (if structural cause)
Use: Assess swallowing mechanism

GI02 | Upper GI bleeding
Category: Gastrointestinal
Modality: Endoscopy - INDICATED [A] FIRST-LINE
Modality: CT Angiography - SPECIFIC CIRCUMSTANCES [B]
Use: CT if active bleeding and endoscopy non-diagnostic

GI03 | Lower GI bleeding
Category: Gastrointestinal
Modality: Colonoscopy - INDICATED [A]
Modality: CT Angiography - SPECIFIC CIRCUMSTANCES [B]
Use: CT if active bleeding

GI04 | Change in bowel habit
Category: Gastrointestinal
Modality: Colonoscopy - INDICATED [A]
Modality: CT Colonography - INDICATED [A]
Age: Urgent referral if >40 with alarm features

GI05 | Acute abdominal pain
Category: Gastrointestinal
Modality: CT Abdomen/Pelvis - INDICATED [A]
Modality: Ultrasound - SPECIFIC CIRCUMSTANCES [B]
Use: US for children, pregnancy, suspected biliary disease

GI06 | Right upper quadrant pain
Category: Gastrointestinal
Modality: Ultrasound - INDICATED [A] FIRST-LINE
Sensitivity: >95% for gallstones
Alternative: MRCP for biliary obstruction

GI07 | Jaundice
Category: Gastrointestinal
Modality: Ultrasound - INDICATED [A] FIRST-LINE
Modality: MRCP - SPECIALISED [B]
Modality: CT - SPECIFIC CIRCUMSTANCES [B]

GI08 | Suspected appendicitis
Category: Gastrointestinal
Modality: CT - INDICATED [A] (adults)
Modality: Ultrasound - INDICATED [B] (children, pregnancy)
Use: Clinical diagnosis, imaging for equivocal cases

GI09 | Small bowel obstruction
Category: Gastrointestinal
Modality: X-ray - INDICATED [A] INITIAL
Modality: CT - INDICATED [A]
Use: X-ray first, CT to determine cause

GI10 | Large bowel obstruction
Category: Gastrointestinal
Modality: X-ray - INDICATED [A] INITIAL
Modality: CT - INDICATED [A]
Use: X-ray first, CT for cause and complications

GI11 | Diverticular disease
Category: Gastrointestinal
Modality: CT - INDICATED [A]
Use: Acute diverticulitis diagnosis and complications

GI12 | Inflammatory bowel disease: initial diagnosis
Category: Gastrointestinal
Modality: Colonoscopy with biopsy - INDICATED [A]
Modality: MRI Enterography - INDICATED [B]
Use: Small bowel Crohn's disease

GI13 | Inflammatory bowel disease: follow-up
Category: Gastrointestinal
Modality: MRI Enterography - INDICATED [B]
Use: Assess disease activity, complications (Crohn's)

[GENITOURINARY - GU CODES]

GU01 | Haematuria: visible
Category: Genitourinary
Modality: CT Urography - INDICATED [A]
Use: Exclude upper tract urothelial malignancy
Adjunct: Cystoscopy for bladder

GU02 | Haematuria: non-visible
Category: Genitourinary
Modality: Ultrasound - SPECIFIC CIRCUMSTANCES [B]
Modality: CT Urography - SPECIFIC CIRCUMSTANCES [B]
Criteria: Age-dependent risk stratification

GU03 | Renal colic: suspected ureteric calculus
Category: Genitourinary
Modality: CT KUB non-contrast - INDICATED [A] GOLD STANDARD
Modality: Ultrasound - INDICATED [B] (pregnancy, young)
Protocol: Low-dose CT when available

GU04 | Urinary tract infection: recurrent (women)
Category: Genitourinary
Modality: Ultrasound - SPECIFIC CIRCUMSTANCES [C]
Indication: Only if structural abnormality suspected

GU05 | Acute urinary retention
Category: Genitourinary
Modality: Ultrasound - INDICATED [B]
Use: Assess post-void residual, upper tract

GU06 | Benign prostatic hyperplasia
Category: Genitourinary
Modality: Ultrasound - SPECIFIC CIRCUMSTANCES [C]
Use: Post-void residual if retention suspected

GU07 | Scrotal pain/swelling: acute
Category: Genitourinary
Modality: Ultrasound with Doppler - INDICATED [A]
Emergency: Testicular torsion (don't delay surgery)

GU08 | Scrotal mass
Category: Genitourinary
Modality: Ultrasound - INDICATED [B]
Use: Characterize mass, guide management

GU09 | Subfertility: male factor
Category: Genitourinary
Modality: Ultrasound - SPECIFIC CIRCUMSTANCES [C]
Use: If varicocele or structural abnormality suspected

GU10 | Pelvic pain: acute (women)
Category: Genitourinary
Modality: Ultrasound - INDICATED [A] FIRST-LINE
Use: Assess ovaries, uterus, free fluid

GU11 | Pelvic mass (women)
Category: Genitourinary
Modality: Ultrasound (TVUS + TA) - INDICATED [A]
Modality: MRI - SPECIALISED [B]
Use: Characterization, surgical planning

GU12 | Postmenopausal bleeding
Category: Genitourinary
Modality: TVUS - INDICATED [A]
Use: Exclude endometrial pathology
Threshold: Endometrial thickness >4mm needs biopsy

GU13 | Heavy menstrual bleeding
Category: Genitourinary
Modality: TVUS - INDICATED [B]
Use: Structural causes (fibroids, polyps)

GU14 | First trimester bleeding/pain
Category: Genitourinary
Modality: TVUS - INDICATED [A]
Use: Ectopic pregnancy, miscarriage, viability

GU15 | Routine antenatal ultrasound
Category: Genitourinary
Modality: Ultrasound - INDICATED [A]
Timing: Dating scan 10-13 weeks, anomaly scan 18-20 weeks

[MUSCULOSKELETAL - M CODES]

M01 | Shoulder pain: suspected rotator cuff tear
Category: Musculoskeletal
Modality: Ultrasound - INDICATED [B] FIRST-LINE
Modality: MRI - INDICATED [B] (if surgery planned)

M02 | Knee pain: internal derangement
Category: Musculoskeletal
Modality: MRI - INDICATED [B]
Use: Meniscal tear, ligament injury

M03 | Hip pain: young adult
Category: Musculoskeletal
Modality: X-ray - INDICATED [A] FIRST-LINE
Modality: MRI - INDICATED [B] (if X-ray normal)

M04 | Hip pain: elderly
Category: Musculoskeletal
Modality: X-ray - INDICATED [A]
Use: Exclude fracture, arthritis

M05 | Low back pain: non-specific
Category: Musculoskeletal
Modality: Imaging - NOT INDICATED [A]
Use: Only if red flags present

M06 | Low back pain: with radiculopathy
Category: Musculoskeletal
Modality: MRI lumbar spine - INDICATED [B]
Timing: After 4-6 weeks conservative management

M07 | Suspected spinal fracture
Category: Musculoskeletal
Modality: X-ray - INDICATED [A] FIRST-LINE
Modality: CT - SPECIFIC CIRCUMSTANCES [B]
Modality: MRI - INDICATED [B] (cord compression suspected)

M08 | Suspected cauda equina syndrome
Category: Musculoskeletal
Modality: MRI lumbar spine - INDICATED [A] URGENT
Use: Emergency diagnosis

M09 | Wrist pain: suspected scaphoid fracture
Category: Musculoskeletal
Modality: X-ray - INDICATED [A] INITIAL
Modality: MRI - INDICATED [B] (if X-ray negative, clinical suspicion)

M10 | Ankle injury: suspected fracture
Category: Musculoskeletal
Modality: X-ray - INDICATED [A]
Criteria: Ottawa ankle rules to determine need

M11 | Suspected stress fracture
Category: Musculoskeletal
Modality: MRI - INDICATED [B]
Modality: Bone scan - SPECIFIC CIRCUMSTANCES [C]

M12 | Suspected osteomyelitis
Category: Musculoskeletal
Modality: MRI - INDICATED [B] INVESTIGATION OF CHOICE
Modality: X-ray - INDICATED [B] (initial, may be normal early)

M13 | Soft tissue mass
Category: Musculoskeletal
Modality: MRI - INDICATED [B]
Use: Characterization before biopsy

M14 | Suspected septic arthritis
Category: Musculoskeletal
Modality: Ultrasound - INDICATED [B]
Use: Detect effusion, guide aspiration

M15 | Inflammatory arthritis: initial assessment
Category: Musculoskeletal
Modality: X-ray hands and feet - INDICATED [B]
Modality: Ultrasound - SPECIFIC CIRCUMSTANCES [B]
Modality: MRI - SPECIFIC CIRCUMSTANCES [B]

[NEUROLOGICAL - N CODES]

N01 | Headache: acute severe (thunderclap)
Category: Neurological
Modality: CT Head non-contrast - INDICATED [A] IMMEDIATE
Use: Exclude subarachnoid hemorrhage

N02 | Headache: chronic/recurrent
Category: Neurological
Modality: MRI Brain - INDICATED [B]
Use: If neurological signs or concerning features

N03 | Stroke: acute ischemic
Category: Neurological
Modality: CT Head non-contrast - INDICATED [A] IMMEDIATE
Modality: CT Angiography - INDICATED [A]
Timing: Within 4.5 hours for thrombolysis

N04 | TIA: suspected
Category: Neurological
Modality: MRI Brain with DWI - INDICATED [B]
Modality: Carotid Doppler - INDICATED [B]

N05 | Seizures: new onset
Category: Neurological
Modality: MRI Brain - INDICATED [B]
Use: Exclude structural cause

N06 | Multiple sclerosis: suspected
Category: Neurological
Modality: MRI Brain and Spine - INDICATED [A]
Protocol: With and without contrast

N07 | Dementia: assessment
Category: Neurological
Modality: CT or MRI Brain - INDICATED [B]
Use: Exclude reversible causes

N08 | Suspected intracranial infection
Category: Neurological
Modality: CT Head - INDICATED [B] (before LP if raised ICP risk)
Modality: MRI - INDICATED [B] (superior sensitivity)

N09 | Vertigo: suspected central cause
Category: Neurological
Modality: MRI Brain - INDICATED [B]
Use: Exclude posterior fossa lesion

N10 | Suspected acoustic neuroma
Category: Neurological
Modality: MRI IAM - INDICATED [B]
Protocol: Thin slice T2, with contrast

[PEDIATRIC - P CODES]

P01 | Head injury: pediatric
Category: Pediatric
Modality: CT Head - SPECIFIC CIRCUMSTANCES [A]
Criteria: CHALICE or PECARN rules
Protocol: Pediatric low-dose if CT necessary

P02 | Non-accidental injury: suspected
Category: Pediatric
Modality: Skeletal survey - INDICATED [B]
Age: <2 years
Additional: CT/MRI brain if head injury

P03 | Developmental dysplasia of hip
Category: Pediatric
Modality: Ultrasound - INDICATED [A] (<6 months)
Modality: X-ray - INDICATED [A] (>6 months)

P04 | Limp: pediatric
Category: Pediatric
Modality: X-ray - INDICATED [B] FIRST-LINE
Modality: Ultrasound - INDICATED [B] (if effusion suspected)
Modality: MRI - SPECIFIC CIRCUMSTANCES [B]

P05 | Abdominal pain: pediatric
Category: Pediatric
Modality: Ultrasound - INDICATED [A] FIRST-LINE
Modality: CT - SPECIFIC CIRCUMSTANCES [B]
Use: Minimize radiation, US preferred

[RESPIRATORY - R CODES]

R01 | Pneumonia: community-acquired
Category: Respiratory
Modality: Chest X-ray - INDICATED [A]
Timing: Can lag clinical findings 12-24 hours

R02 | Pneumonia: hospital-acquired
Category: Respiratory
Modality: Chest X-ray - INDICATED [B]

R03 | Suspected pulmonary tuberculosis
Category: Respiratory
Modality: Chest X-ray - INDICATED [A]

R04 | Chronic cough
Category: Respiratory
Modality: Chest X-ray - INDICATED [B]

R05 | Pleural effusion
Category: Respiratory
Modality: Chest X-ray - INDICATED [A]
Modality: Ultrasound - INDICATED [B] (drainage guidance)

R06 | Pneumothorax: suspected
Category: Respiratory
Modality: Chest X-ray - INDICATED [A]

R07 | Interstitial lung disease: suspected
Category: Respiratory
Modality: HRCT Chest - INDICATED [A]

R08 | Bronchiectasis: suspected
Category: Respiratory
Modality: HRCT Chest - INDICATED [A]

R09 | Solitary pulmonary nodule
Category: Respiratory
Modality: CT Chest - INDICATED [A]
Modality: PET-CT - SPECIFIC CIRCUMSTANCES [B]
Use: Characterization, risk stratification

[TRAUMA - T CODES]

T01 | Head injury: major trauma
Category: Trauma
Modality: CT Head - INDICATED [A] IMMEDIATE
Timing: Within 1 hour for high-risk features

T02 | Cervical spine injury: suspected
Category: Trauma
Modality: CT C-spine - INDICATED [A]
Criteria: Canadian C-spine or NEXUS rules

T03 | Chest trauma: major
Category: Trauma
Modality: CT Chest - INDICATED [A]
Use: Assess mediastinum, great vessels, lungs

T04 | Abdominal trauma: major
Category: Trauma
Modality: CT Abdomen/Pelvis - INDICATED [A]
Protocol: Arterial and portal venous phases

T05 | Pelvic trauma: major
Category: Trauma
Modality: X-ray pelvis - INDICATED [A] INITIAL
Modality: CT Pelvis - INDICATED [A]
Use: CT for definitive assessment

T06 | Long bone fracture: suspected
Category: Trauma
Modality: X-ray - INDICATED [A]
Views: Minimum 2 views (AP and lateral)
Rule: Include joint above and below

T07 | Hand/wrist injury
Category: Trauma
Modality: X-ray - INDICATED [A]
Views: Specific views depending on clinical suspicion

T08 | Foot/ankle injury
Category: Trauma
Modality: X-ray - INDICATED [A]
Criteria: Ottawa ankle/foot rules

================================================================================
SUMMARY STATISTICS
================================================================================

Total Reference Codes: 259
Breast (B): 11 codes
Cancer (CA): 22 codes
Cardiovascular (CV): 9 codes
Gastrointestinal (GI): 13 codes
Genitourinary (GU): 15 codes
Musculoskeletal (M): 15 codes
Neurological (N): 10 codes
Pediatric (P): 5 codes
Respiratory (R): 9 codes
Trauma (T): 8 codes

Evidence Grades:
[A] High-quality evidence: ~40% of recommendations
[B] Moderate-quality evidence: ~50% of recommendations
[C] Limited-quality evidence: ~10% of recommendations

Recommendation Levels:
INDICATED: First-line investigation
SPECIFIC CIRCUMSTANCES: Conditional recommendation
SPECIALISED: Requires specialist consultation
NOT INDICATED: Not recommended

Radiation Safety:
Zero Radiation Modalities: MRI, Ultrasound (preferred when appropriate)
Low Radiation: X-ray, Mammography
Moderate Radiation: CT scans (justify use)
High Radiation: PET-CT (only when essential)

================================================================================
END OF IREFER REFERENCE LIST
================================================================================
`;

/**
 * Anthropic AI Service for clinical data analysis
 * Provides methods to interact with Claude AI for generating accurate medical imaging recommendations
 */
class AnthropicService {
  constructor() {
    this.apiKey = import.meta.env?.VITE_ANTHROPIC_API_KEY;
    this.model = 'claude-sonnet-4-20250514';
  }

  /**
   * Get request headers for Anthropic API
   * @returns {Object} Headers object
   */
  getHeaders() {
    return {
      'Content-Type': 'application/json',
      'x-api-key': this.apiKey,
      'anthropic-version': ANTHROPIC_VERSION,
      'anthropic-dangerous-direct-browser-access': 'true',
    };
  }

  /**
   * Generate clinical imaging recommendation using Claude AI
   * @param {Object} clinicalData - Patient and clinical information
   * @returns {Promise<Object>} AI-generated recommendation
   */
  async generateClinicalRecommendation(clinicalData) {
    try {
      if (!this.apiKey || this.apiKey === 'your-anthropic-api-key-here') {
        throw new Error('Anthropic API key is not configured. Please add VITE_ANTHROPIC_API_KEY to your .env file.');
      }

      // Enhanced prompt with complete iRefer reference database integration
      const prompt = `You are an expert radiologist following the Royal College of Radiologists (RCR) iRefer Guidelines. You have access to the COMPLETE iRefer reference database with 259 codes across all medical specialties.

${IREFER_REFERENCE_DATABASE}

YOUR MISSION:
1. ANALYZE the patient's clinical presentation below
2. MATCH the scenario to the MOST RELEVANT iRefer reference code(s) from the database above
3. PROVIDE a definitive recommendation using the EXACT modality, evidence grade, and rationale from the matched iRefer code
4. ALWAYS cite the specific iRefer code (e.g., "B06", "CA17", "M06", "GU03") in your response
5. Use the evidence grade [A/B/C] from the matched reference
6. Follow the recommendation level (INDICATED/SPECIFIC CIRCUMSTANCES/SPECIALISED/NOT INDICATED)

MANDATORY REQUIREMENTS:
1. NEVER say "can't recommend" or "unable to provide recommendation" - ALWAYS provide a definitive recommendation based on iRefer codes
2. ALWAYS make a clinical recommendation - if exact match unclear, recommend the closest appropriate iRefer guideline
3. ALWAYS reference the specific iRefer code that matches the clinical scenario
4. Use the EXACT modality recommendation from the matched iRefer code
5. Include the evidence grade [A/B/C] from the reference database
6. Explain WHY this iRefer code matches the patient's presentation
7. If insufficient information provided, recommend the most appropriate initial investigation per iRefer guidelines

RADIATION SAFETY PRIORITY (CRITICAL):
- PRIORITIZE radiation-free modalities (MRI, Ultrasound) when iRefer guidelines indicate they are appropriate
- When multiple modalities have equal evidence grades, PREFER zero-radiation options
- For radiating/shooting pain: Follow musculoskeletal iRefer codes (M01-M15) - prioritize MRI when indicated
- Pregnancy: ONLY zero-radiation modalities (Ultrasound/MRI without gadolinium)
- Pediatric: Minimize radiation per pediatric iRefer codes (P01-P05) - prefer US/MRI
- Young adults: Consider lifetime radiation exposure - prefer MRI/US when clinically equivalent

PACEMAKER/IMPLANT CONTRAINDICATIONS:
- Patient has pacemaker/implant: ${clinicalData?.hasPacemaker ? 'YES - MRI CONTRAINDICATED unless MRI-safe device confirmed' : 'NO - No MRI contraindications from cardiac devices'}
- Patient has metal implants: ${clinicalData?.hasMetalImplants ? `YES - Types: ${clinicalData?.metalImplantTypes?.join(', ') || 'Not specified'} - MRI safety depends on implant material (ferromagnetic materials contraindicated)` : 'NO - No metal implant contraindications'}
- If pacemaker present: DO NOT recommend MRI unless explicitly stated as MRI-conditional device
- If ferromagnetic metal implants present: DO NOT recommend MRI (most modern orthopedic implants are titanium/non-ferromagnetic and MRI-safe, but shrapnel/older clips may be ferromagnetic)
- Alternative to MRI when contraindicated: CT (with radiation consideration) or Ultrasound (when appropriate)
- ALWAYS mention pacemaker/metal implant contraindication in rationale if MRI would otherwise be preferred

PATIENT INFORMATION:
- Patient ID: ${clinicalData?.patientId}
- Age: ${clinicalData?.patientAge} years (${clinicalData?.ageGroup})
- Gender: ${clinicalData?.gender}
- Weight: ${clinicalData?.weight || 'Not specified'} kg
- Emergency Status: ${clinicalData?.isEmergency ? 'EMERGENCY - Immediate imaging required' : 'Non-emergency'}
- Pregnancy Status: ${clinicalData?.isPregnant ? 'PREGNANT - Avoid ionizing radiation' : 'Not pregnant'}
- Pacemaker/Implant: ${clinicalData?.hasPacemaker ? 'YES - MRI contraindicated' : 'NO'}
- Metal Implants: ${clinicalData?.hasMetalImplants ? `YES - ${clinicalData?.metalImplantTypes?.join(', ') || 'Types not specified'}` : 'NO'}

CLINICAL PRESENTATION:
- Chief Complaint: ${clinicalData?.chiefComplaint}
- Symptom Duration: ${clinicalData?.symptomDuration}
- Severity: ${clinicalData?.severity}
- Clinical History: ${clinicalData?.clinicalHistory}
- Examination Findings: ${clinicalData?.examinationFindings || 'Not documented'}

IMAGING CONSIDERATIONS:
- Selected Body Region: ${clinicalData?.selectedRegion}

RESPONSE FORMAT (JSON):
{
  "ireferCode": "[Exact code from database, e.g., B06, CA17, M06, GU03]",
  "ireferTitle": "[Full title from the matched code]",
  "primaryModality": "[EXACT modality from matched iRefer code - prioritize radiation-free when appropriate]",
  "alternativeModalities": ["[Alternatives from iRefer code if available]"],
  "evidenceGrade": "[A/B/C from matched code]",
  "recommendationLevel": "[INDICATED/SPECIFIC CIRCUMSTANCES/SPECIALISED/NOT INDICATED]",
  "iReferSection": "[Category from database, e.g., Breast, Cancer/Urological, Musculoskeletal]",
  "urgency": "Emergency/Urgent/Routine",
  "clinicalRationale": "This presentation matches iRefer code [CODE] because [explain match]. The guideline recommends [modality] as [recommendation level] with evidence grade [grade]. [If pacemaker present and MRI contraindicated, explain alternative]. [Radiation safety consideration if applicable]. [Additional clinical reasoning].",
  "radiationSafety": "[Radiation dose and safety considerations from iRefer code - highlight if radiation-free option chosen]",
  "contraindications": ["[List from iRefer code if specified, ALWAYS include pacemaker/MRI contraindication if applicable]"],
  "confidenceScore": 0.95
}

CRITICAL MATCHING INSTRUCTIONS:
1. Search the iRefer database above for keywords matching the patient's symptoms
2. Match by body region, symptom type, age group, and clinical context
3. Use the EXACT code that best fits the clinical scenario
4. If multiple codes apply, choose the most specific one
5. ALWAYS include the iRefer code in your response
6. NEVER refuse to provide a recommendation - find the closest appropriate guideline
7. When radiation-free options (MRI/US) are indicated per iRefer, PRIORITIZE them
8. If pacemaker present, automatically exclude MRI and recommend next best alternative per iRefer

EXAMPLE MATCHING:
- "Breast lump, age 45" → Match to B06 (Breast cancer: diagnosis, Age ≥40)
- "Elevated PSA" → Match to CA17 (Prostate cancer: diagnosis, MRI pre-biopsy) - BUT if pacemaker, recommend TRUS biopsy with US guidance
- "Low back pain with leg radiation" → Match to M06 (Low back pain: with radiculopathy, MRI indicated) - prioritize MRI (radiation-free) unless pacemaker present
- "Flank pain, suspected kidney stone" → Match to GU03 (Renal colic, CT KUB gold standard) - note radiation exposure, US alternative for young/pregnancy

Provide your recommendation now with the matched iRefer code:`;

      const response = await axios?.post(
        ANTHROPIC_API_URL,
        {
          model: this.model,
          max_tokens: 2048,
          temperature: 0.3,
          messages: [
            {
              role: 'user',
              content: prompt
            }
          ]
        },
        { 
          headers: this.getHeaders(),
          timeout: 60000 // 60 second timeout
        }
      );

      const aiResponse = response?.data?.content?.[0]?.text;
      if (!aiResponse) {
        throw new Error('Invalid response from Anthropic API - no content received');
      }

      return this.parseClinicalRecommendation(aiResponse, clinicalData);
    } catch (error) {
      console.error('Anthropic API Error:', error);
      
      // Enhanced error handling with specific error types
      if (error?.response?.status === 401) {
        throw new Error('Invalid Anthropic API key. Please verify your VITE_ANTHROPIC_API_KEY in .env file.');
      }
      
      if (error?.response?.status === 429) {
        throw new Error('API rate limit exceeded. Please wait 60 seconds before trying again.');
      }
      
      if (error?.response?.status === 402 || error?.response?.data?.error?.type === 'insufficient_quota') {
        throw new Error('Your Anthropic API account has insufficient credits. Please add credits to your account at console.anthropic.com or upgrade your plan.');
      }
      
      if (error?.response?.status >= 500) {
        throw new Error('Anthropic service is temporarily unavailable. Please try again in a few minutes.');
      }
      
      if (error?.code === 'ECONNABORTED' || error?.message?.includes('timeout')) {
        throw new Error('Request timeout - the AI service took too long to respond. Please try again.');
      }
      
      throw new Error(
        error?.response?.data?.error?.message || 
        error?.message || 
        'Failed to generate clinical recommendation. Please check your network connection and try again.'
      );
    }
  }

  /**
   * Parse AI response and structure recommendation data
   * @param {string} aiResponse - Raw AI response text
   * @param {Object} clinicalData - Original clinical data
   * @returns {Object} Structured recommendation
   */
  parseClinicalRecommendation(aiResponse, clinicalData) {
    try {
      // Extract JSON from response (Claude sometimes adds formatting)
      const jsonMatch = aiResponse?.match(/\{[\s\S]*\}/);
      if (!jsonMatch) {
        console.error('AI Response without JSON:', aiResponse);
        throw new Error('AI did not return a valid JSON response. The service may be experiencing issues.');
      }

      const parsed = JSON.parse(jsonMatch?.[0]);

      // Validate required fields
      if (!parsed?.primaryModality) {
        throw new Error('AI response missing required field: primaryModality');
      }

      // Enhanced metadata with iRefer code integration
      return {
        id: `REC-${Date.now()}`,
        ireferCode: parsed?.ireferCode || 'GENERAL',
        ireferTitle: parsed?.ireferTitle || 'General iRefer Guidelines',
        primaryModality: parsed?.primaryModality,
        recommendationLevel: parsed?.recommendationLevel || 'INDICATED',
        iReferSection: parsed?.iReferSection || 'General Guidelines',
        evidenceGrade: parsed?.evidenceGrade || 'B',
        urgency: parsed?.urgency || (clinicalData?.isEmergency ? 'Emergency' : 'Routine'),
        clinicalRationale: parsed?.clinicalRationale || 'Evidence-based recommendation per iRefer Guidelines',
        radiationSafety: parsed?.radiationSafety || 'Radiation safety considered per iRefer principles',
        alternativeModalities: parsed?.alternativeModalities || [],
        contraindications: parsed?.contraindications || [],
        confidenceScore: parsed?.confidenceScore || 0.85,
        generatedAt: new Date()?.toISOString(),
        aiProvider: 'Anthropic Claude',
        modelUsed: this.model,
        
        // Legacy fields for backward compatibility
        iReferGrade: parsed?.evidenceGrade || 'B',
        conclusiveStatement: `Based on iRefer code ${parsed?.ireferCode || 'guidelines'}, I recommend ${parsed?.primaryModality}`,
        modalityRationale: parsed?.clinicalRationale,
        radiationDose: parsed?.radiationSafety?.includes('Zero') ? 'Zero radiation' : 'Varies',
        contrastRequired: parsed?.primaryModality?.toLowerCase()?.includes('contrast') || false,
        contrastDetails: parsed?.primaryModality?.toLowerCase()?.includes('contrast') ? 'IV contrast recommended' : 'Not applicable',
        safetyWarnings: parsed?.contraindications || [],
        iReferGuidelines: [`iRefer ${parsed?.ireferCode || 'Guidelines'} - ${parsed?.ireferTitle || 'RCR'}`],
        radiationJustification: parsed?.radiationSafety || 'Per iRefer guidelines',
        iReferCompliance: `Recommendation follows iRefer code ${parsed?.ireferCode || 'guidelines'}`
      };
    } catch (error) {
      console.error('Error parsing AI response:', error);
      if (error instanceof SyntaxError) {
        throw new Error('Failed to parse AI recommendation - invalid JSON format received from API.');
      }
      throw new Error(error?.message || 'Failed to parse AI recommendation. Please try again.');
    }
  }

  /**
   * Stream clinical recommendation with real-time updates
   * @param {Object} clinicalData - Clinical data
   * @param {Function} onChunk - Callback for each text chunk
   * @returns {Promise<Object>} Final recommendation
   */
  async streamClinicalRecommendation(clinicalData, onChunk) {
    try {
      if (!this.apiKey || this.apiKey === 'your-anthropic-api-key-here') {
        throw new Error('Anthropic API key is not configured. Please add VITE_ANTHROPIC_API_KEY to your .env file.');
      }

      const prompt = this.buildClinicalPrompt(clinicalData);

      const response = await fetch(ANTHROPIC_API_URL, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify({
          model: this.model,
          max_tokens: 2048,
          temperature: 0.3,
          stream: true,
          messages: [
            {
              role: 'user',
              content: prompt
            }
          ]
        })
      });

      if (!response?.ok) {
        throw new Error('Network response was not ok');
      }

      const reader = response?.body?.getReader();
      const decoder = new TextDecoder();
      let fullText = '';

      const processStream = async () => {
        while (true) {
          const { done, value } = await reader?.read();
          if (done) break;

          const chunk = decoder?.decode(value);
          const events = chunk?.split('\n\n');

          for (const event of events) {
            const lines = event?.split('\n');
            for (const line of lines) {
              if (line?.startsWith('data: ')) {
                const data = line?.substring(6);
                try {
                  const parsedData = JSON.parse(data);
                  
                  if (parsedData?.type === 'content_block_delta' && 
                      parsedData?.delta?.type === 'text_delta') {
                    const textChunk = parsedData?.delta?.text;
                    fullText += textChunk;
                    if (onChunk) {
                      onChunk(textChunk);
                    }
                  }
                  
                  if (parsedData?.type === 'message_stop') {
                    return fullText;
                  }
                } catch (e) {
                  console.error('Error parsing streaming data:', e);
                }
              }
            }
          }
        }
        return fullText;
      };

      const aiResponse = await processStream();
      return this.parseClinicalRecommendation(aiResponse, clinicalData);
    } catch (error) {
      console.error('Anthropic Streaming Error:', error);
      throw new Error(
        error?.message || 'Failed to stream clinical recommendation'
      );
    }
  }

  /**
   * Count tokens for clinical data (for cost estimation)
   * @param {Object} clinicalData - Clinical data
   * @returns {Promise<number>} Token count
   */
  async countTokens(clinicalData) {
    try {
      if (!this.apiKey || this.apiKey === 'your-anthropic-api-key-here') {
        return 0;
      }

      const prompt = this.buildClinicalPrompt(clinicalData);

      const response = await axios?.post(
        'https://api.anthropic.com/v1/messages/count_tokens',
        {
          model: this.model,
          messages: [{ role: 'user', content: prompt }]
        },
        { headers: this.getHeaders() }
      );

      return response?.data?.total_tokens || 0;
    } catch (error) {
      console.error('Token counting error:', error);
      return 0;
    }
  }
}

export default new AnthropicService();